import * as Radix from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight } from 'lucide-react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';

import { motion } from 'motion/react';
import { useRef } from 'react';

import { cx } from '@/shared/lib/cx';
import { springs } from '@/shared/lib/motion';
import { useMovingHighlight } from '@/shared/lib/useMovingHighlight';

import styles from './DropdownMenu.module.scss';

/**
 * Выпадающее меню поверх примитива Radix.
 *
 * Своими руками такое меню собирается обманчиво легко и почти всегда
 * неправильно: нужны роль `menu`, перемещение стрелками с закольцовыванием,
 * переход к пункту набором первых букв, закрытие по Escape и по клику мимо,
 * возврат фокуса на кнопку, удержание фокуса внутри и переворот меню, когда
 * оно не помещается на экране. Всё это невидимо, пока не сядешь работать с
 * клавиатуры.
 *
 * Своё здесь — только внешность, движение и набор пунктов.
 */
export const DropdownMenu = Radix.Root;

export const DropdownMenuTrigger = Radix.Trigger;

export const DropdownMenuGroup = Radix.Group;

export const DropdownMenuRadioGroup = Radix.RadioGroup;

export const DropdownMenuSub = Radix.Sub;

export type DropdownMenuContentProps = ComponentPropsWithoutRef<typeof Radix.Content>;

export function DropdownMenuContent({
  className,
  sideOffset = 6,
  collisionPadding = 12,
  children,
  ...rest
}: DropdownMenuContentProps) {
  return (
    <Radix.Portal>
      <Radix.Content
        className={cx(styles['menu'], className)}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        {...rest}
      >
        <Highlighted>{children}</Highlighted>
      </Radix.Content>
    </Radix.Portal>
  );
}

/**
 * Подложка под подсвеченным пунктом — одна на меню, переезжает между
 * пунктами (см. useMovingHighlight). Сами пункты фон не красят.
 */
function Highlighted({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const box = useMovingHighlight(ref);

  return (
    <div ref={ref} className={styles['menu__list']}>
      {box && (
        <motion.span
          className={cx(
            styles['menu__highlight'],
            box.tone === 'danger' && styles['menu__highlight--danger'],
          )}
          initial={false}
          animate={{ y: box.top, height: box.height }}
          transition={springs.rubber}
          aria-hidden="true"
        />
      )}
      {children}
    </div>
  );
}

interface ItemDecorations {
  /** Иконка слева. Уточняет действие, но не заменяет подпись. */
  icon?: ReactNode;
  /**
   * Горячая клавиша справа. Только подсказка — само сочетание вешается
   * отдельно, меню о нём не знает.
   */
  shortcut?: string;
  /** `danger` — для удаления и всего необратимого. */
  tone?: 'neutral' | 'danger';
}

export type DropdownMenuItemProps = ComponentPropsWithoutRef<typeof Radix.Item> &
  ItemDecorations & {
    children: ReactNode;
  };

export function DropdownMenuItem({
  icon,
  shortcut,
  tone = 'neutral',
  className,
  children,
  ...rest
}: DropdownMenuItemProps) {
  return (
    <Radix.Item
      className={cx(
        styles['menu__item'],
        tone === 'danger' && styles['menu__item--danger'],
        className,
      )}
      data-tone={tone}
      {...rest}
    >
      {icon !== undefined && (
        <span className={styles['menu__icon']} aria-hidden="true">
          {icon}
        </span>
      )}
      <span className={styles['menu__label']}>{children}</span>
      {shortcut !== undefined && (
        // aria-hidden: скринридер объявляет сочетание сам, из aria-keyshortcuts
        // на кнопке; проговаривать «командная клавиша Е» ещё и здесь незачем.
        <span className={styles['menu__shortcut']} aria-hidden="true">
          {shortcut}
        </span>
      )}
    </Radix.Item>
  );
}

export type DropdownMenuCheckboxItemProps = ComponentPropsWithoutRef<typeof Radix.CheckboxItem> & {
  shortcut?: string;
  children: ReactNode;
};

/** Пункт-флажок: фильтры таблицы, где выбирают несколько значений сразу. */
export function DropdownMenuCheckboxItem({
  className,
  children,
  shortcut,
  ...rest
}: DropdownMenuCheckboxItemProps) {
  return (
    <Radix.CheckboxItem className={cx(styles['menu__item'], className)} {...rest}>
      <span className={styles['menu__label']}>{children}</span>
      {shortcut !== undefined && (
        <span className={styles['menu__shortcut']} aria-hidden="true">
          {shortcut}
        </span>
      )}
      {/* Отметка справа, а не слева: так у пункта с флажком тот же левый
          край, что у обычного, и меню не выглядит ступенькой. */}
      <span className={styles['menu__indicator']}>
        <Radix.ItemIndicator>
          <Check />
        </Radix.ItemIndicator>
      </span>
    </Radix.CheckboxItem>
  );
}

export type DropdownMenuRadioItemProps = ComponentPropsWithoutRef<typeof Radix.RadioItem> &
  Pick<ItemDecorations, 'icon'> & {
    children: ReactNode;
  };

/** Пункт-переключатель: сортировка колонки, выбор темы. */
export function DropdownMenuRadioItem({
  icon,
  className,
  children,
  ...rest
}: DropdownMenuRadioItemProps) {
  return (
    <Radix.RadioItem className={cx(styles['menu__item'], className)} {...rest}>
      {icon !== undefined && (
        <span className={styles['menu__icon']} aria-hidden="true">
          {icon}
        </span>
      )}
      <span className={styles['menu__label']}>{children}</span>
      <span className={styles['menu__indicator']}>
        <Radix.ItemIndicator>
          <Check />
        </Radix.ItemIndicator>
      </span>
    </Radix.RadioItem>
  );
}

export type DropdownMenuLabelProps = ComponentPropsWithoutRef<typeof Radix.Label>;

export function DropdownMenuLabel({ className, ...rest }: DropdownMenuLabelProps) {
  return <Radix.Label className={cx(styles['menu__group-label'], className)} {...rest} />;
}

export type DropdownMenuSeparatorProps = ComponentPropsWithoutRef<typeof Radix.Separator>;

export function DropdownMenuSeparator({ className, ...rest }: DropdownMenuSeparatorProps) {
  return <Radix.Separator className={cx(styles['menu__separator'], className)} {...rest} />;
}

export type DropdownMenuSubTriggerProps = ComponentPropsWithoutRef<typeof Radix.SubTrigger> &
  Pick<ItemDecorations, 'icon'> & {
    children: ReactNode;
  };

export function DropdownMenuSubTrigger({
  icon,
  className,
  children,
  ...rest
}: DropdownMenuSubTriggerProps) {
  return (
    <Radix.SubTrigger className={cx(styles['menu__item'], className)} {...rest}>
      {icon !== undefined && (
        <span className={styles['menu__icon']} aria-hidden="true">
          {icon}
        </span>
      )}
      <span className={styles['menu__label']}>{children}</span>
      <span className={styles['menu__chevron']} aria-hidden="true">
        <ChevronRight size={14} />
      </span>
    </Radix.SubTrigger>
  );
}

export type DropdownMenuSubContentProps = ComponentPropsWithoutRef<typeof Radix.SubContent>;

export function DropdownMenuSubContent({
  className,
  sideOffset = 4,
  collisionPadding = 12,
  ...rest
}: DropdownMenuSubContentProps) {
  return (
    <Radix.Portal>
      <Radix.SubContent
        className={cx(styles['menu'], className)}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        {...rest}
      />
    </Radix.Portal>
  );
}
